import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";

// BrowserAuthError codes that semantically require user interaction to recover.
// Excludes "no_account_error" because that can mean the user is intentionally signed
// out — initiating a redirect would loop.
const RECOVERABLE_BROWSER_AUTH_CODES: ReadonlySet<string> = new Set([
  "monitor_window_timeout",
  "block_iframe_reload",
  "silent_prompt_value_error",
  "no_token_request_cache_error",
  "no_tokens_found",
  "native_account_unavailable",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readStringField(error: Record<string, unknown>, field: string): string | undefined {
  const value = error[field];
  return typeof value === "string" ? value : undefined;
}

// errorCode/errorMessage may live on objects that fail `instanceof` checks because
// multiple copies of @azure/msal-browser are bundled (the app pins ^2.23.0 while
// @azure/msal-react@1.3.2 pulls a transitive 2.38.4). Read fields defensively.
export function isInteractionRequiredError(error: unknown): boolean {
  if (!isRecord(error)) return false;

  if (error instanceof InteractionRequiredAuthError) return true;

  const errorCode = readStringField(error, "errorCode");
  const errorMessage = readStringField(error, "errorMessage");
  const subError = readStringField(error, "subError");

  if (errorCode !== undefined) {
    if (InteractionRequiredAuthError.isInteractionRequiredError(errorCode, errorMessage, subError)) {
      return true;
    }
    if (RECOVERABLE_BROWSER_AUTH_CODES.has(errorCode)) return true;
  }

  return error instanceof BrowserAuthError && RECOVERABLE_BROWSER_AUTH_CODES.has(error.errorCode);
}
